import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap,
  Keyboard,
  Plus,
  Pencil,
  Copy,
  Trash2,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useTrainingModeStore } from '@/store/trainingMode';
import { toast } from 'sonner';

export default function SellSettings() {
  const navigate = useNavigate();
  const { isTrainingMode, enterTrainingMode, exitTrainingMode } = useTrainingModeStore();
  const [quickKeysEnabled, setQuickKeysEnabled] = useState(false);

  const handleToggleTrainingMode = () => {
    if (isTrainingMode) {
      exitTrainingMode();
      toast.success('Training mode disabled');
    } else {
      enterTrainingMode();
      toast.success('Training mode enabled', {
        description: 'No real transactions will be recorded.',
      });
      navigate('/sell/register');
    }
  };

  return (
    <DashboardLayout
      header={
        <h1 className="text-2xl font-display font-semibold tracking-tight">
          Settings
        </h1>
      }
    >
      <p className="text-muted-foreground mb-8">
        Manage your register settings.
      </p>

      <div className="max-w-3xl space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              Training mode
            </CardTitle>
            <CardDescription>
              Enable training mode if you're new to BlazePOS and want to learn the ropes. You'll be
              selling like a pro in no time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant={isTrainingMode ? 'destructive' : 'default'}
              onClick={handleToggleTrainingMode}
            >
              {isTrainingMode ? 'Disable training mode' : 'Enable training mode'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Keyboard className="h-5 w-5" />
              Quick keys
            </CardTitle>
            <CardDescription>
              Assign products as quick keys to help process sales faster. Rename, reposition and
              reorder keys, or organize your buttons into folders and pages.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="quick-keys-toggle" className="font-medium">
                  Enable quick keys for this register
                </Label>
                <p className="text-xs text-muted-foreground">
                  Toggle this switch to browse Quick Keys for your register. You can turn this on
                  and off at anytime without losing your settings.
                </p>
              </div>
              <Switch
                id="quick-keys-toggle"
                checked={quickKeysEnabled}
                onCheckedChange={setQuickKeysEnabled}
              />
            </div>

            {quickKeysEnabled && (
              <>
                <Button variant="default" size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Add layout
                </Button>

                <div className="space-y-3">
                  <QuickKeyLayout
                    name="Default Quick Keys"
                    isCurrent={true}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function QuickKeyLayout({
  name,
  usedByCount,
  isCurrent,
}: {
  name: string;
  usedByCount?: number;
  isCurrent?: boolean;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border p-4">
      <div>
        <div className="flex items-center gap-2">
          <span className="font-medium">{name}</span>
        </div>
        {usedByCount !== undefined && usedByCount > 0 && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Used by {usedByCount} other register{usedByCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {isCurrent ? (
          <Badge variant="secondary">Current Layout</Badge>
        ) : (
          <Button variant="outline" size="sm">
            Set as current layout
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Copy className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
